import axios from 'axios';
import * as jose from 'jose';

// JWT Authentication (same pattern as sheetsService.ts)
let cachedToken: string | null = null;
let tokenExpiry: number | null = null;

const PRIVATE_KEY = process.env.REACT_APP_GOOGLE_SHEETS_PRIVATE_KEY?.replace(/\\n/g, '\n');
const CLIENT_EMAIL = process.env.REACT_APP_GOOGLE_SHEETS_CLIENT_EMAIL;

// Create a JWT token (same as sheetsService.ts)
const createJWT = async (): Promise<{ token: string; expiresIn: number }> => {
  if (!PRIVATE_KEY || !CLIENT_EMAIL) {
    throw new Error('Missing service account credentials');
  }

  const now = Math.floor(Date.now() / 1000);
  const expiresIn = 3600; // 1 hour
  const expiry = now + expiresIn;

  const privateKey = await jose.importPKCS8(PRIVATE_KEY, 'RS256');
  const jwt = await new jose.SignJWT({
    scope: 'https://www.googleapis.com/auth/spreadsheets'
  })
    .setProtectedHeader({ alg: 'RS256', typ: 'JWT' })
    .setIssuedAt(now)
    .setIssuer(CLIENT_EMAIL)
    .setSubject(CLIENT_EMAIL)
    .setAudience('https://sheets.googleapis.com/')
    .setExpirationTime(expiry)
    .sign(privateKey);

  return { token: jwt, expiresIn };
};

// Get a valid token, using cache if available (same as sheetsService.ts)
const getAuthToken = async (): Promise<string> => {
  if (cachedToken && tokenExpiry && Date.now() < tokenExpiry) {
    return cachedToken;
  }

  const { token, expiresIn } = await createJWT();
  cachedToken = token;
  tokenExpiry = Date.now() + (expiresIn * 1000);
  return token;
};

// Google Sheets API constants
const BASE_URL = 'https://sheets.googleapis.com/v4/spreadsheets';

/**
 * Fetches raw data from a specific sheet range within a Google Spreadsheet.
 * Includes checks for spreadsheet access and sheet existence.
 *
 * @param spreadsheetId The ID of the Google Sheets spreadsheet.
 * @param sheetName The name of the sheet to fetch data from.
 * @param rangeA1 The A1 notation of the range to fetch (e.g., 'A1:KM').
 * @returns A promise resolving to a 2D array of sheet data, or an empty array if not found/error.
 * @throws Error if authentication fails, spreadsheet/sheet is inaccessible, or API call fails.
 */
export const fetchSheetData = async (spreadsheetId: string, sheetName: string, rangeA1: string): Promise<any[][]> => {
  const authToken = await getAuthToken();

  if (!authToken) {
    throw new Error('Authentication failed: No auth token available.');
  }

  try {
    const sheetRange = encodeURIComponent(`${sheetName}!${rangeA1}`);

    // Optional: Check spreadsheet access and sheet existence first (can be heavy)
    // Consider removing this check if performance is critical and relying on the values.get error handling
    // try {
    //   const metadataResponse = await axios.get(`${BASE_URL}/${spreadsheetId}`, { headers: { 'Authorization': `Bearer ${authToken}` } });
    //   const sheets = metadataResponse.data.sheets || [];
    //   const targetSheet = sheets.find((sheet: any) => sheet.properties?.title?.toLowerCase() === sheetName.toLowerCase());
    //   if (!targetSheet) {
    //     throw new Error(`Sheet "${sheetName}" not found in spreadsheet.`);
    //   }
    // } catch (metaError) {
    //    console.error("Error checking metadata before fetching sheet data:", metaError);
    //    // Decide whether to throw or proceed cautiously
    //    throw new Error(`Failed to verify sheet existence before fetching data: ${metaError instanceof Error ? metaError.message : 'Unknown metadata error'}`);
    // }

    // Fetch the actual data
    const response = await axios.get(
      `${BASE_URL}/${spreadsheetId}/values/${sheetRange}?valueRenderOption=UNFORMATTED_VALUE`, // Always fetch unformatted
      {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        }
      }
    );

    if (!response.data.values) {
      return [];
    }

    return response.data.values;
  } catch (error) {

    if (axios.isAxiosError(error)) {

      if (error.response?.status === 403) {
        throw new Error(`Access denied fetching sheet data. Ensure spreadsheet is shared with service account (Editor access).`);
      } else if (error.response?.status === 404) {
        throw new Error(`Spreadsheet or sheet range not found. Check ID, sheet name, and range.`);
      }
       throw new Error(`API error fetching sheet data: ${error.response?.data?.error?.message || error.message}`);
    } else if (error instanceof Error) {
       throw new Error(`Error fetching sheet data: ${error.message}`);
    } else {
       throw new Error('Unknown error occurred while fetching sheet data.');
    }
  }
};